import {
  Archive,
  ArrowLeft,
  Building2,
  CheckCircle2,
  Circle,
  DollarSign,
  Download,
  Mail,
  Phone,
  User,
} from 'lucide-react'
import { useMemo } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ClientBuckets } from './clients/ClientBuckets'
import { ClientDocuments } from './ClientDocuments'
import { BentoCard } from '../components/BentoCard'
import { Button } from '../components/Button'
import { Dropdown } from '../components/Dropdown'
import { EmptyState } from '../components/EmptyState'
import { ProgressBar } from '../components/ProgressBar'
import { StatCard } from '../components/StatCard'
import { StatusBadge } from '../components/StatusBadge'
import { useAppData } from '../context/AppDataContext'
import { useToast } from '../context/ToastContext'
import { useScopedWork } from '../hooks/useScopedWork'
import { DONE_STATUS, ENGAGEMENTS, type AccountStatus, type Engagement } from '../data/types'
import { downloadCsv } from '../lib/csv'
import { useAuth } from '../context/AuthContext'
import { formatCurrency, formatDate, formatDateLong, monthName } from '../lib/format'
import { MonthStepper } from '../components/Requirements'
import { CheckSquare, Clock3, FolderKanban, PencilLine } from 'lucide-react'
import { ClientPeopleAndPlan } from './clients/ClientPeopleAndPlan'
import { DeliverablesBoard } from './clients/DeliverablesBoard'

const accountStatuses: AccountStatus[] = ['Prospect', 'Active', 'Unconfirmed', 'Former', 'Lost']


export default function ClientDetail() {
  const { id } = useParams()
  const { toggleDeliverable, updateClient, archiveClient, directory, loading, month, setMonth } = useAppData()
  const navigate = useNavigate()
  const { clients, projects, deliverables } = useScopedWork()
  const { showToast } = useToast()
  // An Admin can switch exporting off per role in the permissions matrix.
  const canExport = useAuth().hasPermission('clients', 'export')
  const client = clients.find((c) => c.id === id)

  const clientProjects = useMemo(() => projects.filter((p) => p.clientId === id), [projects, id])
  const clientDeliverables = useMemo(() => deliverables.filter((d) => d.clientId === id), [deliverables, id])
  // Coworker names/avatars for a client you're already staffed on come from
  // the non-sensitive directory, not the (persona-scoped) `team` list — the
  // latter only ever contains your own record for a Team Member/Viewer.
  const assignedTeam = useMemo(() => directory.filter((m) => client?.assignedTeamIds.includes(m.id)), [directory, client])

  if (!client) {
    // Still loading is not the same as not there: say so rather than flash "not found".
    if (loading) return <p className="py-16 text-center text-sm text-shell-text">Loading…</p>
    return <EmptyState title="Client not found" description="This client may have been removed." />
  }

  // Pieces, not rows. This counted commitments whose status is 'Completed',
  // and nothing sets that status, so the bar sat at 0% for a client who had
  // shipped half the month.
  const promised = clientDeliverables.reduce((sum, d) => sum + d.target, 0)
  const shipped = clientDeliverables.reduce((sum, d) => sum + d.delivered, 0)
  const progressPct = promised === 0 ? 0 : Math.min(Math.round((shipped / promised) * 100), 100)

  function exportCsv() {
    downloadCsv(
      `${client!.name.replace(/\s+/g, '_')}_data.csv`,
      clientDeliverables.map((d) => ({
        Name: d.name,
        Type: d.type,
        DueDate: d.dueDate,
        Status: d.status,
        AssignedTo: directory.find((m) => m.id === d.assignedToId)?.name ?? '',
        CompletionDate: d.completionDate ?? '',
      })),
    )
    showToast('Client data exported as CSV.')
  }

  return (
    <div className="flex flex-col gap-5">
      <Link to="/clients" className="inline-flex items-center gap-1.5 text-sm font-medium text-canvas-fg/60 hover:text-canvas-fg">
        <ArrowLeft size={15} /> Back to clients
      </Link>

      <div className="flex flex-col gap-4 rounded-card border border-shell-border bg-shell p-6 shadow-card sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <div
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-lg font-bold text-white"
            style={{ backgroundColor: client.color }}
          >
            {client.initials}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-xl font-bold text-shell-text-active">{client.name}</h1>
              <Dropdown
                trigger={<StatusBadge status={client.accountStatus} className="cursor-pointer hover:opacity-80" />}
                items={accountStatuses.map((s) => ({ label: s, value: s }))}
                onSelect={(v) => {
                  updateClient(client.id, { accountStatus: v as AccountStatus })
                  showToast(`Account status changed to ${v}.`)
                }}
              />
              <Dropdown
                trigger={
                  <span className="cursor-pointer rounded-full border border-shell-border px-2 py-0.5 text-xs font-medium text-shell-text hover:text-shell-text-active">
                    {client.engagement}
                  </span>
                }
                items={ENGAGEMENTS.map((e) => ({ label: e, value: e }))}
                onSelect={(v) => {
                  updateClient(client.id, { engagement: v as Engagement })
                  showToast(`${client.name} is now ${v === 'One-off' ? 'a one-off' : 'a recurring'} client.`)
                }}
              />
              {/*
                * Every figure on this page is one month's: the cards, the
                * board, the deliverables. They followed the app's month with
                * no way to change it here, so September — work that was
                * recorded afterwards — could not be looked at from the client
                * it belongs to. The month is the app's own, so stepping it here
                * is the same month the Content plan and Analytics show.
                */}
              <MonthStepper month={month} onChange={setMonth} className="ml-1" />
            </div>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-shell-text">
              <Building2 size={13} /> {client.industry}
            </p>
            {/*
              * Contact details are not on screen, and not because they are
              * missing: phone numbers are the one piece of client PII this
              * platform holds, they live in `client_contacts` for the WhatsApp
              * agent to match against, and a header that prints one puts it on
              * every screenshot anybody takes of this page.
              *
              * The retainer is `undefined` rather than null for anybody who is
              * not a partner — the server drops the key on the way out — so
              * this renders nothing at all rather than a dash, which would read
              * as "nothing agreed" instead of "not yours to see".
              */}
            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-shell-text">
              <span className="flex items-center gap-1"><User size={12} /> {client.pendingDeliverables} outstanding in {monthName(month)}</span>
              {client.monthlyRetainer !== undefined && (
                <span className="flex items-center gap-1">
                  <DollarSign size={12} /> {formatCurrency(client.monthlyRetainer, client.retainerCurrency)}/mo
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {/*
            * Straight into this client's sheet, which is where a month actually
            * gets planned. The same write mode as the Content plan, not a second
            * grid on this page: one sheet means one set of keyboard gestures,
            * one undo, and one place a row can be wrong. Asked for because
            * everybody who opens a client page to plan their month was going
            * back out to Content plan and picking the client again.
            */}
          <Button variant="secondary" onClick={() => navigate(`/content/sheet?client=${encodeURIComponent(client.id)}`)}>
            <PencilLine size={15} /> Write mode
          </Button>
          {canExport && (
            <Button variant="secondary" onClick={exportCsv}>
              <Download size={15} /> Download as CSV
            </Button>
          )}
          {/* Put away once the work is done. Nothing is deleted, and the
              Archive tab on Clients brings them back. */}
          <Button
            variant="ghost"
            onClick={async () => {
              await archiveClient(client.id)
              showToast(`${client.name} moved to the archive. Bring them back from Clients, Archive.`)
              navigate('/clients')
            }}
          >
            <Archive size={15} /> Archive
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Cycles open" value={client.activeProjects} icon={FolderKanban} accent="#3b7dde" index={0} />
        <StatCard label="Went out" value={client.completedDeliverables} icon={CheckSquare} accent="#2f9e57" index={1} />
        <StatCard label="Still owed" value={client.pendingDeliverables} icon={Clock3} accent="#d98e1c" index={2} />
        <StatCard label="On this client" value={assignedTeam.length} icon={User} accent="#FC2265" index={3} />
      </div>

      <ClientPeopleAndPlan client={client} />

      <BentoCard title="Board">
        <DeliverablesBoard clientId={client.id} color={client.color} />
      </BentoCard>

      <div className="grid grid-cols-1 gap-4">
        <BentoCard title={monthName(month)}>
          <div className="flex flex-col gap-3.5">
            {clientProjects.map((p) => (
              <div key={p.id} className="rounded-xl border border-canvas-fg/10 p-3.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-canvas-fg">{p.name}</p>
                  <StatusBadge status={p.status} />
                </div>
                <p className="mt-1 text-xs text-canvas-fg/60">{p.description}</p>
                <div className="mt-2.5 flex items-center gap-3">
                  <ProgressBar value={p.progress} color={client.color} height={5} trackClassName="bg-canvas-fg/15" />
                  <span className="w-9 shrink-0 text-right text-xs font-semibold text-canvas-fg/70">{p.progress}%</span>
                </div>
                <p className="mt-2 text-xs text-canvas-fg/50">Due {formatDate(p.dueDate)}</p>
              </div>
            ))}
            {clientProjects.length === 0 && <p className="py-6 text-center text-sm text-canvas-fg/50">No projects yet.</p>}
          </div>
        </BentoCard>
      </div>

      <ClientDocuments clientId={client.id} />

      <ClientBuckets clientId={client.id} clientName={client.name} />

      <BentoCard
        title="Deliverables"
        description={`${shipped} of ${promised} posted`}
        action={
          <div className="w-32">
            <ProgressBar value={progressPct} color={client.color} height={6} showLabel trackClassName="bg-canvas-fg/15" />
          </div>
        }
      >
        {/*
          * Read-only, and it has to be.
          *
          * The design gave every row a tick and a "Mark Complete" button. A
          * commitment is not a checkbox — it says a client is owed eight reels
          * this month, and it is satisfied by eight pieces reaching `posted`,
          * which the server counts. Ticking it by hand would let the number on
          * the money screen disagree with the work that actually shipped.
          *
          * `toggleDeliverable` has been a deliberate no-op for that reason.
          * What was left was worse than the button: it toasted "Deliverable
          * marked as complete." over a call that did nothing, so the one
          * person who tried it was told it had worked. The row shows the count
          * instead, and the way to move it is to post a piece.
          */}
        <div className="divide-y divide-canvas-fg/10">
          {clientDeliverables.map((d) => {
            const done = d.target > 0 && d.delivered >= d.target
            const pct = d.target === 0 ? 0 : Math.min(Math.round((d.delivered / d.target) * 100), 100)
            return (
              <div key={d.id} className="flex flex-wrap items-center gap-3 py-3.5 first:pt-0 last:pb-0">
                <span className="text-canvas-fg/30" aria-hidden="true">
                  {done ? <CheckCircle2 size={19} className="text-success-500" /> : <Circle size={19} />}
                </span>
                <div className="min-w-[160px] flex-1">
                  <p className={`text-sm font-medium ${done ? 'text-canvas-fg/40' : 'text-canvas-fg'}`}>{d.name}</p>
                  <p className="text-xs text-canvas-fg/50">{d.type}</p>
                </div>
                <span className="text-xs text-canvas-fg/60 w-24">Due {formatDate(d.dueDate)}</span>
                <div className="flex w-40 items-center gap-2">
                  <ProgressBar value={pct} color={done ? '#2f9e57' : client.color} height={5} trackClassName="bg-canvas-fg/15" />
                  <span className="shrink-0 text-xs font-semibold text-canvas-fg/60 tabular-nums">
                    {d.delivered}/{d.target}
                  </span>
                </div>
                {d.inFlight > 0 && (
                  <span className="text-xs text-canvas-fg/50">{d.inFlight} in flight</span>
                )}
                <StatusBadge status={d.status} />
              </div>
            )
          })}
          {clientDeliverables.length === 0 && <p className="py-6 text-center text-sm text-canvas-fg/50">Nothing promised in {monthName(month)}.</p>}
        </div>
      </BentoCard>
    </div>
  )
}
